import axios, {
  AxiosRequestConfig,
  AxiosError,
  Method,
  AxiosRequestHeaders,
  InternalAxiosRequestConfig, // <--- IMPORT QUAN TRỌNG
} from "axios";
import { AppError, ErrorCode } from "@mebi/shared";
import { translateErrorCode } from "./lib/errorTranslator";
import { Preferences } from "@capacitor/preferences";

// Define types for Vite env
interface ImportMetaEnv {
  VITE_API_URL?: string;
  [key: string]: unknown;
}

const trimTrailingSlashes = (value: string): string => {
  let result = value;
  while (result.endsWith("/")) {
    result = result.slice(0, -1);
  }
  return result;
};

const stripApiSuffix = (value: string): string => value.replace(/\/api$/i, "");

const normalizeBaseUrl = (value: string): string => {
  const trimmed = trimTrailingSlashes(value.trim());
  if (!trimmed) return "";
  return stripApiSuffix(trimmed);
};

// Helper to get Vite URL safely
const getViteUrl = (): string | null => {
  try {
    if (
      import.meta !== undefined &&
      (import.meta as unknown as { env: ImportMetaEnv }).env?.VITE_API_URL
    ) {
      return (import.meta as unknown as { env: ImportMetaEnv }).env
        .VITE_API_URL;
    }
  } catch {
    /* Ignore */
  }
  return null;
};

const getNextUrl = (): string | null => {
  try {
    if (typeof process !== "undefined") {
      if (process.env?.NEXT_PUBLIC_API_URL) {
        return process.env.NEXT_PUBLIC_API_URL;
      }
      if (process.env?.VITE_API_URL) {
        return process.env.VITE_API_URL;
      }
    }
  } catch {
    /* Ignore */
  }
  return null;
};

export const normalizeApiPath = (path: string): string => {
  if (!path) {
    return "/api";
  }

  if (/^https?:\/\//i.test(path)) {
    return path;
  }

  if (path === "/api" || path.startsWith("/api/")) {
    return path;
  }

  if (path.startsWith("/")) {
    return `/api${path}`;
  }

  return `/api/${path}`;
};

const getBrowserOrigin = (): string | null => {
  try {
    if (typeof globalThis !== "undefined" && globalThis.location?.origin) {
      const origin = globalThis.location.origin.trim();
      if (/^https?:\/\//i.test(origin)) {
        return origin;
      }
    }
  } catch {
    /* Ignore */
  }
  return null;
};

export const getBaseUrl = (): string => {
  const viteUrl = getViteUrl();
  if (viteUrl) {
    const normalized = normalizeBaseUrl(viteUrl);
    if (normalized) {
      return normalized;
    }
  }

  const nextUrl = getNextUrl();
  if (nextUrl) {
    const normalized = normalizeBaseUrl(nextUrl);
    if (normalized) {
      return normalized;
    }
  }

  const browserOrigin = getBrowserOrigin();
  if (browserOrigin) {
    return normalizeBaseUrl(browserOrigin);
  }

  return normalizeBaseUrl("http://localhost:3011");
};

export const AXIOS_INSTANCE = axios.create({
  baseURL: getBaseUrl(),
  timeout: 30000,
  withCredentials: true,
  headers: {
    "Content-Type": "application/json",
  },
});

/**
 * Request interceptor
 * FIX: Sử dụng 'InternalAxiosRequestConfig' để headers luôn được định nghĩa sẵn.
 */
AXIOS_INSTANCE.interceptors.request.use(
  async (config: InternalAxiosRequestConfig) => {
    if (typeof config.url === "string") {
      config.url = normalizeApiPath(config.url);
    }

    // Không cần dòng này nữa: config.headers = config.headers || {};
    // Axios đảm bảo config.headers luôn tồn tại trong InternalAxiosRequestConfig
    // FIX: Sử dụng static import thay vì dynamic import
    try {
      const { value: token } = await Preferences.get({ key: "access_token" });

      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
    } catch {
      if (
        typeof globalThis !== "undefined" &&
        globalThis.window?.localStorage
      ) {
        const token = globalThis.window.localStorage.getItem("access_token");
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
      }
    }
    return config;
  },
);

function extractApiErrorDetails(
  data: unknown,
  fallbackCode: ErrorCode = ErrorCode.UNKNOWN_ERROR,
): { code: ErrorCode; message: string } {
  const errorData = data as
    | { errorCode?: string; message?: string | string[] }
    | undefined;

  const serverErrorCode = errorData?.errorCode as ErrorCode | undefined;
  const finalCode =
    serverErrorCode && Object.values(ErrorCode).includes(serverErrorCode)
      ? serverErrorCode
      : fallbackCode;

  let serverMessage = "";
  if (errorData?.message) {
    serverMessage = Array.isArray(errorData.message)
      ? errorData.message[0]
      : errorData.message;
  }

  return {
    code: finalCode,
    message: serverMessage || translateErrorCode(finalCode),
  };
}

AXIOS_INSTANCE.interceptors.response.use(
  (response) => response,
  async (error: AxiosError<unknown>) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & {
      _retry?: boolean;
    };

    if (!error.response) {
      const isTimeout =
        error.code === "ECONNABORTED" || error.message?.includes("timeout");
      const code = isTimeout ? ErrorCode.TIMEOUT : ErrorCode.NETWORK_ERROR;

      throw new AppError(translateErrorCode(code), {
        code,
        originalError: error,
        isOperational: true,
      });
    }

    const { status, data } = error.response;

    if (status === 401 && originalRequest && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        await Preferences.remove({ key: "access_token" });
      } catch {
        if (
          typeof globalThis !== "undefined" &&
          globalThis.window?.localStorage
        ) {
          globalThis.window.localStorage.removeItem("access_token");
        }
      }
    }

    const { code: finalCode, message } = extractApiErrorDetails(data);

    throw new AppError(message, {
      code: finalCode,
      originalError: error,
      context:
        typeof data === "object" && data !== null
          ? (data as Record<string, unknown>)
          : { data },
      isOperational: true,
    });
  },
);

export interface CustomRequestInit extends Omit<RequestInit, "body"> {
  body?: RequestInit["body"];
}

export const customInstance = async <T>(
  url: string,
  options?: CustomRequestInit,
): Promise<T> => {
  const source = axios.CancelToken.source();

  const config: AxiosRequestConfig = {
    url,
    method: (options?.method as Method) || "GET",
    // Chỗ này vẫn cần ép kiểu vì options.headers của fetch khác với axios
    headers: options?.headers as unknown as AxiosRequestHeaders,
    data: options?.body,
    signal: options?.signal,
    cancelToken: source.token,
  };

  const response = await AXIOS_INSTANCE(config);

  return {
    data: response.data,
    status: response.status,
    headers: response.headers as unknown as Headers,
  } as T;
};

export type ErrorType<Error> = Error;
export type BodyType<BodyData> = BodyData;
